import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { toast } from 'react-toastify'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import {
  cancelCaseDispatchRequest,
  createCaseDispatchRequestForBookedEvent,
  dispatchCaseDispatchLive,
  expireCaseDispatchRequest,
  extractCaseDispatchEligibility,
  extractCaseDispatchOffers,
  extractCaseDispatchPolicy,
  extractCaseDispatchRequest,
  generateCaseDispatchDryRunOffers,
  getCaseDispatchRequest,
  listCaseDispatchRequests,
  previewCaseDispatchEligibilityForBookedEvent,
  regenerateCaseDispatchDryRunOffers,
} from 'src/supportPortal/services/caseDispatchService'

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'OPEN', label: 'Open' },
  { value: 'OFFERING', label: 'Offering' },
  { value: 'CLAIMED', label: 'Claimed' },
  { value: 'BOOKED', label: 'Booked' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'FAILED', label: 'Failed' },
]

const REQUEST_STATUS_COLORS = {
  DRAFT: 'default',
  OPEN: 'info',
  OFFERING: 'warning',
  CLAIMED: 'success',
  BOOKED: 'success',
  CANCELLED: 'error',
  EXPIRED: 'default',
  FAILED: 'error',
}

const OFFER_STATUS_COLORS = {
  ELIGIBLE: 'info',
  CONTACTED: 'warning',
  DELIVERED: 'warning',
  SEEN: 'warning',
  ACCEPTED: 'success',
  REJECTED: 'default',
  WITHDRAWN: 'default',
  EXPIRED: 'default',
  CLAIM_LOST: 'error',
  FAILED: 'error',
}

function getPayload(response) {
  return response?.data || response?.response?.data || response || {}
}

function getDataMap(response) {
  return getPayload(response)?.data || {}
}

function getResponseMessage(response, fallback = 'Request failed') {
  return getPayload(response)?.message || fallback
}

function getPageContent(pageOrList) {
  if (Array.isArray(pageOrList)) return pageOrList
  if (Array.isArray(pageOrList?.content)) return pageOrList.content
  return []
}

function formatDateTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

function formatText(value, fallback = '-') {
  if (value == null || value === '') return fallback
  return String(value)
}

function getEligibilitySnapshot(source) {
  return source?.eligibilitySnapshot || source || {}
}

function getEligibleCandidates(eligibility) {
  const snapshot = getEligibilitySnapshot(eligibility)
  return Array.isArray(snapshot?.eligibleCandidates) ? snapshot.eligibleCandidates : []
}

function getExcludedCandidates(eligibility) {
  const snapshot = getEligibilitySnapshot(eligibility)
  return Array.isArray(snapshot?.excludedCandidates) ? snapshot.excludedCandidates : []
}

function getPractitionerLabel(candidate) {
  return candidate?.practitionerName || candidate?.practitionerId || candidate?.practitionerUserId || 'Practitioner'
}

function reasonText(reasons = []) {
  if (!Array.isArray(reasons) || reasons.length === 0) return '-'
  return reasons.join(', ')
}

function ResponseAlert({ error }) {
  if (!error) return null
  return (
    <Alert severity="error" variant="outlined">
      {error}
    </Alert>
  )
}

function Section({ title, action, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </Paper>
  )
}

function CandidateTable({ title, candidates, emptyLabel, showExcluded = false }) {
  return (
    <Stack spacing={1.25}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {candidates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      ) : (
        <TableContainer component={Box} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Practitioner</TableCell>
                <TableCell>Practitioner ID</TableCell>
                <TableCell>{showExcluded ? 'Exclude reasons' : 'Include reasons'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.map((candidate, index) => (
                <TableRow key={`${candidate?.practitionerId || candidate?.practitionerUserId || index}`}>
                  <TableCell>{getPractitionerLabel(candidate)}</TableCell>
                  <TableCell>{formatText(candidate?.practitionerId)}</TableCell>
                  <TableCell>
                    {reasonText(showExcluded ? candidate?.excludeReasons : candidate?.includeReasons)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  )
}

export default function CaseDispatchConsolePage() {
  const [bookedEventId, setBookedEventId] = useState('')
  const [createStatus, setCreateStatus] = useState('DRAFT')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterBookedEventId, setFilterBookedEventId] = useState('')
  const [requestsPage, setRequestsPage] = useState(null)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [selectedOffers, setSelectedOffers] = useState([])
  const [previewResult, setPreviewResult] = useState(null)
  const [previewPolicy, setPreviewPolicy] = useState(null)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState('')

  const requests = useMemo(() => getPageContent(requestsPage), [requestsPage])
  const selectedEligibility = selectedRequest?.eligibilitySnapshot || previewResult
  const eligibleCandidates = getEligibleCandidates(selectedEligibility)
  const excludedCandidates = getExcludedCandidates(selectedEligibility)
  const pageTotal = requestsPage?.totalElements ?? requests.length

  const loadRequests = async (overrides = {}) => {
    setLoading(true)
    setError('')
    const params = {
      page: 0,
      pageSize: 25,
      ...(filterStatus ? { status: filterStatus } : {}),
      ...(filterBookedEventId ? { bookedEventId: filterBookedEventId.trim() } : {}),
      ...overrides,
    }
    try {
      const response = await listCaseDispatchRequests(params)
      if (!isCommonResponseSuccessfulV2(response)) {
        setError(getResponseMessage(response, 'Could not load case dispatch requests'))
        return
      }
      setRequestsPage(getDataMap(response)?.['case-dispatch-request'])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRequests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePreview = async () => {
    if (!bookedEventId.trim()) {
      setError('Booked event id is required')
      return
    }
    setActionLoading(true)
    setError('')
    try {
      const response = await previewCaseDispatchEligibilityForBookedEvent(bookedEventId.trim())
      if (!isCommonResponseSuccessfulV2(response)) {
        setError(getResponseMessage(response, 'Eligibility preview failed'))
        return
      }
      setPreviewResult(extractCaseDispatchEligibility(response))
      setPreviewPolicy(extractCaseDispatchPolicy(response))
      setSelectedRequest(null)
      setSelectedOffers([])
      toast.success('Eligibility preview loaded')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCreateRequest = async () => {
    if (!bookedEventId.trim()) {
      setError('Booked event id is required')
      return
    }
    setActionLoading(true)
    setError('')
    try {
      const response = await createCaseDispatchRequestForBookedEvent(bookedEventId.trim(), {
        status: createStatus,
      })
      if (!isCommonResponseSuccessfulV2(response)) {
        setError(getResponseMessage(response, 'Dispatch request could not be saved'))
        return
      }
      const request = extractCaseDispatchRequest(response)
      setSelectedRequest(request)
      setSelectedOffers([])
      setPreviewResult(extractCaseDispatchEligibility(response))
      setPreviewPolicy(extractCaseDispatchPolicy(response))
      await loadRequests()
      toast.success(response?.data?.data?.duplicate ? 'Existing dispatch request opened' : 'Dispatch request saved')
    } finally {
      setActionLoading(false)
    }
  }

  const loadRequestDetail = async (requestId) => {
    if (!requestId) return
    setActionLoading(true)
    setError('')
    try {
      const response = await getCaseDispatchRequest(requestId, { includeOffers: true })
      if (!isCommonResponseSuccessfulV2(response)) {
        setError(getResponseMessage(response, 'Could not load dispatch request'))
        return
      }
      setSelectedRequest(extractCaseDispatchRequest(response))
      setSelectedOffers(extractCaseDispatchOffers(response))
      setPreviewResult(null)
      setPreviewPolicy(null)
    } finally {
      setActionLoading(false)
    }
  }

  const refreshSelectedFromResponse = async (response, successMessage) => {
    if (!isCommonResponseSuccessfulV2(response)) {
      setError(getResponseMessage(response))
      return
    }
    const request = extractCaseDispatchRequest(response)
    if (request) {
      setSelectedRequest(request)
      setSelectedOffers(extractCaseDispatchOffers(response))
      await loadRequests()
      toast.success(successMessage)
    }
  }

  const handleGenerateOffers = async () => {
    if (!selectedRequest?.id) return
    setActionLoading(true)
    setError('')
    try {
      const response = await generateCaseDispatchDryRunOffers(selectedRequest.id)
      await refreshSelectedFromResponse(response, 'Dry-run offers generated')
    } finally {
      setActionLoading(false)
    }
  }

  const handleRegenerateOffers = async () => {
    if (!selectedRequest?.id) return
    setActionLoading(true)
    setError('')
    try {
      const response = await regenerateCaseDispatchDryRunOffers(selectedRequest.id, {
        reason: 'Support regenerated dry-run offers',
        refreshEligibility: true,
      })
      await refreshSelectedFromResponse(response, 'Dry-run offers regenerated')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!selectedRequest?.id) return
    setActionLoading(true)
    setError('')
    try {
      const response = await cancelCaseDispatchRequest(selectedRequest.id, {
        reason: 'Cancelled by support',
      })
      await refreshSelectedFromResponse(response, 'Dispatch request cancelled')
    } finally {
      setActionLoading(false)
    }
  }

  const handleExpire = async () => {
    if (!selectedRequest?.id) return
    setActionLoading(true)
    setError('')
    try {
      const response = await expireCaseDispatchRequest(selectedRequest.id, {
        reason: 'Expired by support',
      })
      await refreshSelectedFromResponse(response, 'Dispatch request expired')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDispatchLive = async () => {
    if (!selectedRequest?.id) return
    const confirmed = window.confirm('Send live case dispatch communications to eligible practitioners?')
    if (!confirmed) return
    setActionLoading(true)
    setError('')
    try {
      const response = await dispatchCaseDispatchLive(selectedRequest.id, {
        reason: 'Support started live dispatch',
        generateDryRunOffersIfMissing: true,
      })
      await refreshSelectedFromResponse(response, 'Live dispatch started')
    } finally {
      setActionLoading(false)
    }
  }

  const canMutateSelected = ['DRAFT', 'OPEN'].includes(selectedRequest?.status)

  return (
    <Box sx={{ minHeight: '100%', backgroundColor: '#f7f8fb', pb: 4 }}>
      <SupportHeader
        heading="Case Dispatch"
        subHeading="Operations"
      />
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5 }}>
        <Stack spacing={2.5}>
          <ResponseAlert error={error} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(360px, 0.8fr) minmax(0, 1.4fr)' },
              gap: 2.5,
              alignItems: 'start',
            }}
          >
            <Stack spacing={2.5}>
              <Section title="Preview And Save">
                <Stack spacing={2}>
                  <TextField
                    label="Booked event id"
                    size="small"
                    value={bookedEventId}
                    onChange={(event) => setBookedEventId(event.target.value)}
                    fullWidth
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel id="case-dispatch-create-status-label">Initial status</InputLabel>
                    <Select
                      labelId="case-dispatch-create-status-label"
                      label="Initial status"
                      value={createStatus}
                      onChange={(event) => setCreateStatus(event.target.value)}
                    >
                      <MenuItem value="DRAFT">Draft</MenuItem>
                      <MenuItem value="OPEN">Open</MenuItem>
                    </Select>
                  </FormControl>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<SearchOutlinedIcon />}
                      onClick={handlePreview}
                      disabled={actionLoading}
                      fullWidth
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PlaylistAddCheckOutlinedIcon />}
                      onClick={handleCreateRequest}
                      disabled={actionLoading}
                      fullWidth
                    >
                      Save
                    </Button>
                  </Stack>
                </Stack>
              </Section>

              <Section
                title="Request Filters"
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshOutlinedIcon />}
                    onClick={() => loadRequests()}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                }
              >
                <Stack spacing={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="case-dispatch-status-filter-label">Status</InputLabel>
                    <Select
                      labelId="case-dispatch-status-filter-label"
                      label="Status"
                      value={filterStatus}
                      onChange={(event) => setFilterStatus(event.target.value)}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value || 'all'} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Booked event id"
                    size="small"
                    value={filterBookedEventId}
                    onChange={(event) => setFilterBookedEventId(event.target.value)}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    startIcon={<SearchOutlinedIcon />}
                    onClick={() => loadRequests()}
                    disabled={loading}
                  >
                    Search
                  </Button>
                </Stack>
              </Section>
            </Stack>

            <Section
              title={`Dispatch Requests (${pageTotal})`}
              action={loading ? <CircularProgress size={22} /> : null}
            >
              {requests.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No case dispatch requests found.
                </Typography>
              ) : (
                <TableContainer component={Box} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>Booked event</TableCell>
                        <TableCell>Case</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>Eligible</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.map((request) => (
                        <TableRow
                          key={request?.id}
                          selected={request?.id === selectedRequest?.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => loadRequestDetail(request?.id)}
                        >
                          <TableCell>
                            <Chip
                              size="small"
                              label={formatText(request?.status)}
                              color={REQUEST_STATUS_COLORS[request?.status] || 'default'}
                              variant={request?.status === 'DRAFT' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell>{formatText(request?.bookedEventId)}</TableCell>
                          <TableCell>
                            <Stack spacing={0.25}>
                              <Typography variant="body2">{formatText(request?.eventType)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatText(request?.discipline)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{formatDateTime(request?.startTime)}</TableCell>
                          <TableCell>{request?.eligiblePractitionerIds?.length || 0}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="View request">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  loadRequestDetail(request?.id)
                                }}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          </Box>

          <Section
            title={selectedRequest ? `Request ${selectedRequest.id}` : 'Eligibility Snapshot'}
            action={
              selectedRequest ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<HubOutlinedIcon />}
                    onClick={handleGenerateOffers}
                    disabled={actionLoading || !canMutateSelected}
                  >
                    Generate
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshOutlinedIcon />}
                    onClick={handleRegenerateOffers}
                    disabled={actionLoading || !canMutateSelected}
                  >
                    Regenerate
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<SendOutlinedIcon />}
                    onClick={handleDispatchLive}
                    disabled={actionLoading || !canMutateSelected}
                  >
                    Send live
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<HourglassEmptyOutlinedIcon />}
                    onClick={handleExpire}
                    disabled={actionLoading || !canMutateSelected}
                  >
                    Expire
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<CancelOutlinedIcon />}
                    onClick={handleCancel}
                    disabled={actionLoading || !canMutateSelected}
                  >
                    Cancel
                  </Button>
                </Stack>
              ) : null
            }
          >
            {actionLoading ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Working
                </Typography>
              </Stack>
            ) : null}

            {selectedRequest ? (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    label={selectedRequest.status || 'UNKNOWN'}
                    color={REQUEST_STATUS_COLORS[selectedRequest.status] || 'default'}
                  />
                  <Chip label={`Booked event: ${formatText(selectedRequest.bookedEventId)}`} variant="outlined" />
                  <Chip label={`Event: ${formatText(selectedRequest.eventType)}`} variant="outlined" />
                  <Chip label={`Brand: ${formatText(selectedRequest.clinicBrandName || selectedRequest.clinicBrandId)}`} variant="outlined" />
                  <Chip label={`Location: ${formatText(selectedRequest.clinicLocationName || selectedRequest.clinicLocationId)}`} variant="outlined" />
                </Stack>
                <Divider />
              </Stack>
            ) : null}

            {!selectedRequest && !previewResult ? (
              <Typography variant="body2" color="text.secondary">
                Select a request or run an eligibility preview.
              </Typography>
            ) : (
              <Stack spacing={2.5}>
                {previewPolicy ? (
                  <Alert severity="info" variant="outlined">
                    Policy {previewPolicy?.id || 'default'} v{previewPolicy?.version || '-'}
                  </Alert>
                ) : null}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <CandidateTable
                    title={`Eligible Practitioners (${eligibleCandidates.length})`}
                    candidates={eligibleCandidates}
                    emptyLabel="No eligible practitioners."
                  />
                  <CandidateTable
                    title={`Excluded Practitioners (${excludedCandidates.length})`}
                    candidates={excludedCandidates}
                    emptyLabel="No excluded practitioners in this snapshot."
                    showExcluded
                  />
                </Box>

                {selectedRequest ? (
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Offers ({selectedOffers.length})
                    </Typography>
                    {selectedOffers.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No offers generated for this request.
                      </Typography>
                    ) : (
                      <TableContainer component={Box} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Status</TableCell>
                              <TableCell>Practitioner</TableCell>
                              <TableCell>Round</TableCell>
                              <TableCell>Run</TableCell>
                              <TableCell>Dry run</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedOffers.map((offer) => (
                              <TableRow key={offer?.id || `${offer?.practitionerId}-${offer?.roundNumber}`}>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={formatText(offer?.status)}
                                    color={OFFER_STATUS_COLORS[offer?.status] || 'default'}
                                    variant={offer?.status === 'ELIGIBLE' ? 'outlined' : 'filled'}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={0.25}>
                                    <Typography variant="body2">
                                      {formatText(offer?.practitionerName || offer?.practitionerId)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatText(offer?.practitionerUserId)}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>{offer?.roundNumber || '-'}</TableCell>
                                <TableCell>{formatText(offer?.eligibilityRunId)}</TableCell>
                                <TableCell>
                                  {offer?.dryRun ? (
                                    <Chip size="small" icon={<CheckCircleOutlineIcon />} label="Yes" color="info" variant="outlined" />
                                  ) : (
                                    <Chip size="small" label="No" color="warning" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Stack>
                ) : null}
              </Stack>
            )}
          </Section>
        </Stack>
      </Box>
    </Box>
  )
}
